/**
 * Anmeldeablauf — Port von `Services/LoginService.cs` und `AuthService.Login/GetApps`.
 *
 * Der Zustand liegt bewusst in einem veränderlichen Objekt: Der Altclient liest und schreibt
 * die Felder von `AuthService` innerhalb eines Ablaufs mehrfach. Der `AuthProvider` spiegelt
 * das Objekt nach jedem Ablauf in den React-Zustand.
 */

import type { AccountInfo } from '@azure/msal-browser';

import { graphApi } from '@/api/graphApi';
import { ApiError } from '@/api/httpClient';
import { authApi } from '@/api/services/authApi';
import type { App, ChinaAppSysGroup, ChinaAppSysUser, ChinaAppSysView } from '@/types/entities';
import { createChinaAppSysUser } from '@/types/entities';

import { readStoredCurrentApp, writeStoredCurrentApp } from './currentAppStorage';
import { getAuthenticatedAccount } from './msalInstance';
import { navigateTo } from './navigation';

export interface AuthSession {
  authorized: boolean;
  apps: App[] | null;
  currentApp: App | null;
  user: ChinaAppSysUser | null;
  groups: ChinaAppSysGroup[] | null;
  views: ChinaAppSysView[] | null;
  profileImage: string;
  company: string;
}

export function createAuthSession(): AuthSession {
  return {
    authorized: false,
    apps: null,
    // Erster Zweig von `LoadCurrentApp`: ohne gewählte App gilt die zuletzt gespeicherte.
    currentApp: readStoredCurrentApp(),
    user: null,
    groups: null,
    views: null,
    profileImage: '',
    company: '',
  };
}

/** Port von `LoginService.LoadCurrentApp`. */
function loadCurrentApp(session: AuthSession): void {
  if (session.currentApp === null) {
    session.currentApp = readStoredCurrentApp();
  } else {
    writeStoredCurrentApp(session.currentApp);
  }
}

/** Pendant zu `user.Claims.FirstOrDefault(e => e.Type.ToLower() == claimType)`. */
function readClaim(account: AccountInfo, claimType: string): string | null {
  const claims: Record<string, unknown> = account.idTokenClaims ?? {};
  const claim = Object.entries(claims).find(([type]) => type.toLowerCase() === claimType);
  const value = claim?.[1];
  return typeof value === 'string' ? value : null;
}

/**
 * Port von `AuthService.GetApps()`: Die Liste wird nur bei Erfolg übernommen, andernfalls
 * bleibt die zuletzt geladene erhalten. Nur echte Netzwerkfehler erreichen den Aufrufer.
 */
export async function loadApps(session: AuthSession): Promise<App[]> {
  try {
    session.apps = await authApi.getApps();
  } catch (error) {
    if (!(error instanceof ApiError)) {
      throw error;
    }
  }
  return session.apps ?? [];
}

/** Port von `AuthService.Login()`. */
async function loadAppsAndUser(session: AuthSession): Promise<boolean> {
  try {
    const apps = await authApi.getApps();
    session.apps = apps;

    // Bleibt die Antwort aus, scheitert `some` — im Altclient die NullReferenceException,
    // die der umschließende catch-Block zu `false` macht.
    if (!apps.some((app) => app.URI.toLowerCase() === session.currentApp?.URI.toLowerCase())) {
      session.currentApp = null;
    }
    if (session.currentApp === null) {
      return true;
    }

    const user = await authApi.getMe(session.currentApp.URI);
    if (user === null) {
      session.user = null;
      session.groups = null;
      return false;
    }

    session.user = user;
    const groups = await authApi.getMyGroups(session.currentApp.URI);
    if (groups !== null) {
      session.groups = groups;
    }
    return true;
  } catch {
    return false;
  }
}

/** Port von `LoginService.LoginAsync`. */
export async function performLogin(session: AuthSession): Promise<boolean> {
  loadCurrentApp(session);

  const account = getAuthenticatedAccount();
  if (account === null) {
    return false;
  }

  const name = readClaim(account, 'name');
  const email = readClaim(account, 'preferred_username');
  const oid = readClaim(account, 'oid');
  if (name === null || email === null || oid === null) {
    return false;
  }

  session.user = { ...createChinaAppSysUser(), Oid: oid, EMail: email, Name: name };

  if (await loadAppsAndUser(session)) {
    if ((session.apps ?? []).length === 0) {
      navigateTo('/authentication/error/no-apps');
    } else if (session.currentApp === null) {
      navigateTo('/authentication/apps');
    }

    session.profileImage = await graphApi.getProfileImage();
    session.company = await graphApi.getCompanyName();
    return true;
  }

  if (session.apps === null || session.apps.length === 0) {
    navigateTo('/authentication/error/no-apps');
  } else if (session.user === null) {
    writeStoredCurrentApp(null);
    navigateTo('/authentication/error/no-user');
  }

  return false;
}
